<template>
  <section class="py-12 sm:py-24 bg-slate-900 relative overflow-hidden border-b border-slate-800" dir="rtl">
    <!-- Dynamic Background Glow based on value -->
    <div
      class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[150%] max-w-3xl h-[600px] rounded-full pointer-events-none transition-[background] duration-700 ease-out"
      :style="{ background: `radial-gradient(circle, rgba(236,72,153,${0.1 + (sliderPercent / 100) * 0.3}) 0%, transparent 70%)` }"
    ></div>

    <div class="max-w-3xl mx-auto px-4 relative z-10">
      <div class="text-center mb-8">
        <h2 class="text-3xl md:text-5xl font-black text-white mb-3 tracking-tight">
          השקעה שמחזירה <span class="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-purple-400">את עצמה מיד.</span>
        </h2>
        <p class="text-slate-400 text-sm sm:text-base">
          שחקו עם כמות התורים וגלו בעצמכם כמה עולה לכם <span
          class="underline decoration-pink-500 underline-offset-4 font-bold text-slate-300">לא</span> להשתמש בטיימאיט.
        </p>
      </div>

      <!-- The Dashboard Widget -->
      <div
        class="bg-slate-800/90 rounded-[2rem] border border-slate-700 shadow-2xl p-6 sm:p-10 relative overflow-hidden flex flex-col items-center">

        <!-- Top: The Big Number -->
        <div class="text-center mb-8 w-full">
          <p class="text-pink-400 font-bold text-xs sm:text-sm mb-2 tracking-widest uppercase">השווי החודשי שתקבלו
            חזרה</p>
          <div
            class="inline-flex items-center justify-center text-transparent bg-clip-text bg-gradient-to-b from-white to-pink-100 drop-shadow-[0_0_20px_rgba(236,72,153,0.3)]">
            <span class="text-7xl sm:text-9xl font-black tracking-tighter leading-none">{{ formattedTotalValue }}</span>
            <span class="text-4xl sm:text-5xl font-bold ml-2 opacity-80 text-white"
                  :style="`transform: translateY(${isMobile ? '40' : '80'}%)`">₪</span>
          </div>

          <!-- Explanation below the number -->
          <div
            class="flex flex-col sm:flex-row items-center justify-center gap-2 mt-6 p-4 bg-white/5 rounded-2xl border border-white/10">
            <p class="text-slate-300 text-sm font-medium text-center">
              איך חישבנו? החזרנו לכם <strong class="text-purple-400">{{ hoursSaved }} שעות בחודש</strong> ששווים
              ₪{{ formattedValueOfTimeSaved }},<br class="hidden sm:block" />
              ומנענו <strong class="text-emerald-400">{{ noShowsPrevented }} הברזות</strong> ששוות
              ₪{{ formattedValueOfNoShows }}.
            </p>
          </div>
        </div>

        <!-- Middle: The Interactive Slider with Bubble -->
        <div class="w-full max-w-lg mx-auto relative pt-12 pb-6">

          <!-- Floating Bubble -->
          <div
            class="absolute top-0 flex flex-col items-center transition-all duration-150 ease-out z-20"
            :style="{ right: `calc(${sliderPercent}%)`, transform: 'translateX(50%)' }"
          >
            <div
              class="bg-gradient-to-r from-emerald-400 to-emerald-500 text-slate-900 font-black text-[11px] sm:text-sm px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl shadow-[0_0_20px_rgba(16,185,129,0.4)] whitespace-nowrap">
              מחזירים את ההשקעה פי {{ roiMultiplier }}!
            </div>
            <!-- Bubble Triangle pointer -->
            <div
              class="w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] sm:border-l-[8px] sm:border-r-[8px] sm:border-t-[8px] border-l-transparent border-r-transparent border-t-emerald-500 mt-[-1px]"></div>
          </div>

          <div class="flex justify-between items-end mb-3" dir="rtl">
            <span class="text-slate-300 font-bold text-sm">כמה תורים יש לכם בחודש?</span>
            <div class="flex items-baseline gap-1">
              <span class="text-2xl sm:text-3xl font-black text-white leading-none">{{ appointmentsPerMonth }}</span>
            </div>
          </div>

          <!-- RTL Custom Slider Wrapper -->
          <div class="relative w-full h-3 sm:h-4 bg-slate-900 rounded-full border border-slate-800 shadow-inner"
               dir="rtl">
            <div
              class="absolute top-0 right-0 h-full bg-gradient-to-l from-purple-500 to-pink-500 rounded-full transition-all duration-150 ease-out shadow-[0_0_10px_rgba(236,72,153,0.5)]"
              :style="{ width: `${sliderPercent}%` }"
            ></div>
            <input
              type="range"
              min="20"
              max="300"
              step="10"
              dir="rtl"
              v-model.number="appointmentsPerMonth"
              class="absolute top-0 right-0 w-full h-full opacity-0 cursor-pointer z-20"
            />
            <div
              class="absolute top-1/2 -translate-y-1/2 w-6 h-6 sm:w-8 sm:h-8 bg-white rounded-full shadow-[0_0_15px_rgba(236,72,153,1)] border-[3px] border-pink-500 pointer-events-none transition-all duration-150 ease-out flex items-center justify-center z-10"
              :style="{ right: `calc(${sliderPercent}% )`, transform: 'translate(50%, -50%)' }"
            >
              <div class="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-pink-500 rounded-full"></div>
            </div>
          </div>

          <!-- Explanation text below slider -->
          <div class="mt-8 text-center bg-slate-900/40 p-4 rounded-xl border border-slate-800">
            <p class="text-slate-300 text-sm font-bold mb-3 flex items-center justify-center gap-2">
              איך הגענו ל-6 דקות חיסכון לתור?
            </p>
            <div class="flex flex-col sm:flex-row justify-center items-center gap-1 sm:gap-4">
              <span
                class="bg-slate-800 px-3 py-1.5 rounded-lg border border-slate-700 text-xs text-slate-300 w-full sm:w-auto">2 דק' קביעה בוואטסאפ</span>
              <span class="text-slate-600 sm:block">+</span>
              <span
                class="bg-slate-800 px-3 py-1.5 rounded-lg border border-slate-700 text-xs text-slate-300 w-full sm:w-auto">2 דק' תזכורת ואישור</span>
              <span class="text-slate-600 sm:block">+</span>
              <span
                class="bg-slate-800 px-3 py-1.5 rounded-lg border border-slate-700 text-xs text-slate-300 w-full sm:w-auto">2 דק' הפקת חשבונית</span>
            </div>
          </div>
        </div>

      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useResponsive } from '@app/composables/responsive';

const { isMobile } = useResponsive();

// --- State ---
const appointmentsPerMonth = ref(120);

// --- Constants ---
const hourlyRate = 140; // 140 NIS per hour
const avgApptPrice = 150; // 150 NIS per appt
const minsSavedPerAppt = 6; // Realistic 6 mins saved per appt
const noShowRate = 0.03; // Conservative 3% no shows prevented
const systemCost = 119; // Monthly cost
const minAppts = 20;
const maxAppts = 300;

// --- Computed Properties ---
const hoursSaved = computed(() => {
  return Math.round((appointmentsPerMonth.value * minsSavedPerAppt) / 60);
});

const valueOfTimeSaved = computed(() => {
  return hoursSaved.value * hourlyRate;
});

const noShowsPrevented = computed(() => {
  return Math.max(1, Math.round(appointmentsPerMonth.value * noShowRate));
});

const valueOfNoShows = computed(() => {
  return noShowsPrevented.value * avgApptPrice;
});

const totalMonthlyValue = computed(() => {
  return valueOfTimeSaved.value + valueOfNoShows.value;
});

const roiMultiplier = computed(() => {
  return Math.round(totalMonthlyValue.value / systemCost);
});

const sliderPercent = computed(() => {
  return ((appointmentsPerMonth.value - minAppts) / (maxAppts - minAppts)) * 100;
});

// Formatting
const formattedTotalValue = computed(() => totalMonthlyValue.value.toLocaleString());
const formattedValueOfTimeSaved = computed(() => valueOfTimeSaved.value.toLocaleString());
const formattedValueOfNoShows = computed(() => valueOfNoShows.value.toLocaleString());
</script>
